import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  ShieldCheck, 
  Lock, 
  DatabaseZap, 
  Clock, 
  FileLock2, 
  BookLock, 
  Cookie, 
  Cloud, 
  Gauge, 
  ExternalLink,
  BookOpen, 
  ListChecks, 
  Users, 
  TrendingUp, 
  Globe, 
  Briefcase,
  CheckCircle,
  FileJson,
  Code 
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  initial: "initial",
  whileInView: "animate",
  viewport: { once: true, amount: 0.1 },
  variants: {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  }
};

const trustCategories = [
  {
    title: "Secure by Design",
    icon: ShieldCheck,
    items: [
      { text: "SSL Secured (HTTPS)", icon: Lock },
      { text: "Data encrypted in transit & at rest", icon: DatabaseZap },
      { text: "Token-based API authentication with TTL", icon: FileLock2 },
      { text: "No permanent file storage — auto-delete after 60 minutes", icon: Clock },
    ],
    description: "Our platform is built with security at its core, employing industry-standard practices to protect your data and ensure safe operations."
  },
  {
    title: "Compliant & Transparent",
    icon: BookLock,
    items: [
      { text: "GDPR Compliant", icon: CheckCircle },
      { text: "CCPA Aware", icon: CheckCircle },
      { text: "Cookie Consent implemented", icon: Cookie, link: "/cookies", linkText: "View Cookie Policy", ariaLabel: "View WebFiny Cookie Policy" },
      { text: "Hosted on Google Cloud (Cloud Run, Firestore, GCS)", icon: Cloud },
      { text: "99.9% target uptime", icon: Gauge, linkText: "View Status", linkAction: "View Status Page", ariaLabel: "View WebFiny System Status" },
    ],
    description: "We adhere to global compliance standards and maintain transparency about our operations and data handling policies."
  },
  {
    title: "Developer-First Platform",
    icon: Code,
    items: [
      { text: "Public API on Postman", icon: FileJson, link: "https://www.postman.com/finycore/webfiny/overview", external: true, linkText: "View on Postman", ariaLabel: "View WebFiny API on Postman" },
      { text: "Detailed changelog & API reference docs", icon: BookOpen, link: "/changelog", linkText: "View Changelog", ariaLabel: "View WebFiny Changelog" },
      { text: "Built-in rate limiting, credit-based fairness", icon: ListChecks },
      { text: "Usage logs available to users", icon: ListChecks, linkAction: "View Usage Logs (Dashboard)", linkText: "View Logs", ariaLabel: "View Usage Logs in WebFiny Dashboard" },
    ],
    description: "Designed for developers, with comprehensive documentation, robust APIs, and tools to streamline your integration."
  },
  {
    title: "Trusted & Growing",
    icon: Users,
    items: [
      { text: "Over 100,000 PDFs processed every week", icon: TrendingUp },
      { text: "Serving users in 12+ countries", icon: Globe },
      { text: "Built for SaaS teams, print providers, and developers", icon: Briefcase },
    ],
    description: "Join a growing community of developers and businesses who rely on Webfiny for their PDF automation needs."
  }
];

const trustBadges = [
  { text: "SSL Secure", icon: Lock },
  { text: "GDPR Compliant", icon: CheckCircle },
  { text: "Powered by Google Cloud", icon: Cloud },
  { text: "Postman API Publisher", icon: FileJson }
];

const TrustSection = ({ onActionClick }) => {
  const handleLinkClick = (item) => {
    if (item.linkAction) {
      onActionClick(item.linkAction);
    } else if (item.external && item.link) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section id="trust" className="py-20 bg-background border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-heading text-primary-foreground">
            Built on <span className="gradient-text-brand">Trust & Credibility</span>
          </h2>
          <p className="text-xl text-foreground max-w-3xl mx-auto">
            We prioritize security, compliance, and transparency to provide a reliable platform for your PDF needs.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          {...staggerContainer}
        >
          {trustBadges.map((badge, index) => (
            <motion.div
              key={index}
              className="bg-card p-4 rounded-lg shadow-lg border border-border flex items-center justify-center space-x-2 hover-lift"
              variants={fadeInUp}
            >
              <badge.icon className="h-5 w-5 text-primary" />
              <span className="text-sm text-card-foreground font-medium">{badge.text}</span>
            </motion.div>
          ))}
        </motion.div>

        <div className="hidden md:grid md:grid-cols-1 lg:grid-cols-2 gap-8">
          {trustCategories.map((category, index) => (
            <motion.div
              key={index}
              className="bg-card p-8 rounded-xl shadow-xl border border-border hover-lift flex flex-col"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeInUp}
            >
              <div className="flex items-center mb-6">
                <category.icon className="h-10 w-10 text-primary mr-4" />
                <h3 className="text-2xl font-bold text-card-foreground font-heading">{category.title}</h3>
              </div>
              <p className="text-muted-foreground text-sm mb-6 flex-grow">{category.description}</p>
              <ul className="space-y-3 mb-6">
                {category.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center text-sm text-foreground">
                    <item.icon className="h-4 w-4 text-green-500 mr-3 shrink-0" />
                    <span>{item.text}</span>
                    {item.link && !item.external && !item.linkAction && (
                      <Link to={item.link} className="ml-2 text-primary hover:underline text-xs" aria-label={item.ariaLabel || item.linkText}>
                        {item.linkText || <ExternalLink className="h-3 w-3"/>}
                      </Link>
                    )}
                    {(item.link && (item.external || item.linkAction)) && (
                       <Button variant="link" size="sm" className="p-0 h-auto ml-2 text-primary hover:underline text-xs" onClick={() => handleLinkClick(item)} aria-label={item.ariaLabel || item.linkText}>
                         {item.linkText || <ExternalLink className="h-3 w-3"/>}
                       </Button>
                    )}
                  </li>
                ))}
              </ul>
              {category.title === "Secure by Design" && (
                 <Link to="/security" aria-label="View Full WebFiny Security Details">
                    <Button variant="outline" className="w-full mt-auto border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                        View Full Security Details
                    </Button>
                 </Link>
              )}
            </motion.div>
          ))}
        </div>
        
        <div className="md:hidden">
          <Accordion type="multiple" className="w-full space-y-4">
            {trustCategories.map((category, index) => (
              <motion.div 
                key={index} 
                initial="initial"
                whileInView="animate"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeInUp}
              >
                <AccordionItem value={`item-${index}`} className="bg-card rounded-xl shadow-xl border border-border p-2">
                  <AccordionTrigger className="hover:no-underline text-left">
                    <div className="flex items-center">
                      <category.icon className="h-8 w-8 text-primary mr-3" />
                      <span className="text-xl font-bold text-card-foreground font-heading">{category.title}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-4">
                    <p className="text-muted-foreground text-sm mb-4 px-2">{category.description}</p>
                    <ul className="space-y-3 mb-4 px-2">
                      {category.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center text-sm text-foreground">
                          <item.icon className="h-4 w-4 text-green-500 mr-3 shrink-0" />
                          <span>{item.text}</span>
                          {item.link && !item.external && !item.linkAction && (
                            <Link to={item.link} className="ml-2 text-primary hover:underline text-xs" aria-label={item.ariaLabel || item.linkText}>
                              {item.linkText || <ExternalLink className="h-3 w-3"/>}
                            </Link>
                          )}
                          {(item.link && (item.external || item.linkAction)) && (
                             <Button variant="link" size="sm" className="p-0 h-auto ml-2 text-primary hover:underline text-xs" onClick={() => handleLinkClick(item)} aria-label={item.ariaLabel || item.linkText}>
                               {item.linkText || <ExternalLink className="h-3 w-3"/>}
                             </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                    {category.title === "Secure by Design" && (
                       <Link to="/security" className="block px-2 pb-2" aria-label="View Full WebFiny Security Details">
                          <Button variant="outline" className="w-full mt-auto border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                              View Full Security Details
                          </Button>
                       </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>

      </div>
    </section>
  );
};

export default TrustSection;